// ═══════════════════════════════════════════════════════════════
// InfrastructureDashboard — Monitoring services ITRIIA
// Session 32 — Admin Infrastructure fonctionnel
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Server, Database, HardDrive, Cloud, Workflow, Cpu, RefreshCw } from 'lucide-react';
import ActiveUsersPanel from './ActiveUsersPanel';

const serviceConfig = {
  postgresql: { label: 'PostgreSQL', icon: <Database size={22} />, color: '#3b82f6' },
  redis: { label: 'Redis', icon: <HardDrive size={22} />, color: '#ef4444' },
  weaviate: { label: 'Weaviate', icon: <Cpu size={22} />, color: '#8b5cf6' },
  nextcloud: { label: 'ITRIIA Cloud', icon: <Cloud size={22} />, color: '#10b981' },
  n8n: { label: 'N8N Workflows', icon: <Workflow size={22} />, color: '#f59e0b' },
  server: { label: 'Serveur Node.js', icon: <Server size={22} />, color: '#06b6d4' },
};

const statusDot = (status) => {
  const colors = {
    ok: '#10b981',
    down: '#ef4444',
    degraded: '#f59e0b',
    timeout: '#f59e0b',
    unknown: '#6b7280',
    error: '#ef4444',
    not_ready: '#f59e0b',
  };
  const color = colors[status] || '#6b7280';
  const isOk = status === 'ok';
  return (
    <span style={{
      width: '10px', height: '10px', borderRadius: '50%',
      background: color, display: 'inline-block',
      boxShadow: isOk ? `0 0 8px ${color}60` : 'none',
      animation: isOk ? 'infraPulse 2s ease-in-out infinite' : 'none',
    }} />
  );
};

const statusLabel = (status) => {
  const labels = {
    ok: 'En ligne', down: 'Hors ligne', degraded: 'Dégradé',
    timeout: 'Timeout', unknown: 'Inconnu', error: 'Erreur', not_ready: 'Non prêt',
  };
  return labels[status] || status;
};

const InfrastructureDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadInfrastructure = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch('/api/admin/infrastructure', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Erreur infrastructure:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadInfrastructure(); }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement...</div>;
  if (!data) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Aucune donnee disponible</div>;

  const cardStyle = {
    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
    borderRadius: '12px', padding: '20px',
  };

  const allOk = Object.values(data.services).every(s => s.status === 'ok');
  const downCount = Object.values(data.services).filter(s => s.status === 'down' || s.status === 'error').length;

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <style>{`
        @keyframes infraPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Server size={22} style={{ color: '#3b82f6' }} />
            Infrastructure ITRIIA
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Monitoring services en temps reel
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Global status badge */}
          <div style={{
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
            display: 'flex', alignItems: 'center', gap: '6px',
            background: allOk ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            color: allOk ? '#10b981' : '#ef4444',
            border: `1px solid ${allOk ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
          }}>
            {statusDot(allOk ? 'ok' : 'down')}
            {allOk ? 'Tous les services OK' : `${downCount} service(s) en erreur`}
          </div>
          <button onClick={() => loadInfrastructure(true)} style={{
            padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
            cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', gap: '6px',
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: 'white',
          }}>
            <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            Rafraichir
          </button>
        </div>
      </div>

      {/* Session 36 — Utilisateurs actifs live */}
      <ActiveUsersPanel />

      {/* Service cards grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' }}>
        {Object.entries(data.services).map(([key, service]) => {
          const config = serviceConfig[key] || { label: key, icon: <Server size={22} />, color: '#6b7280' };
          return (
            <div key={key} style={{
              ...cardStyle,
              borderLeft: `3px solid ${service.status === 'ok' ? config.color : service.status === 'down' ? '#ef4444' : '#f59e0b'}`,
            }}>
              {/* Service header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '10px',
                    background: `${config.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {React.cloneElement(config.icon, { style: { color: config.color } })}
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text)' }}>{config.label}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{service.version || 'N/A'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {statusDot(service.status)}
                  <span style={{
                    fontSize: '11px', fontWeight: '600',
                    color: service.status === 'ok' ? '#10b981' : service.status === 'down' ? '#ef4444' : '#f59e0b',
                  }}>
                    {statusLabel(service.status)}
                  </span>
                </div>
              </div>

              {/* Metrics */}
              {service.metrics && Object.keys(service.metrics).length > 0 && (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '8px', padding: '12px', borderRadius: '8px',
                  background: 'var(--input-bg)',
                }}>
                  {Object.entries(service.metrics).map(([mKey, mVal]) => {
                    const metricLabels = {
                      database_size: 'Taille DB', active_users: 'Users actifs', projects: 'Projets',
                      usage_events: 'Events', uptime: 'Uptime', memory: 'Memoire', keys: 'Cles',
                      ready: 'Pret', objects: 'Objets', url: 'URL', healthy: 'Sante',
                      heap_used: 'Heap utilise', heap_total: 'Heap total', rss: 'RSS',
                      load_avg: 'Load avg', cpus: 'CPUs', error: 'Erreur',
                    };
                    return (
                      <div key={mKey}>
                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                          {metricLabels[mKey] || mKey}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)', wordBreak: 'break-all' }}>
                          {typeof mVal === 'boolean' ? (mVal ? 'Oui' : 'Non') : String(mVal)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Timestamp */}
      <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '11px', color: 'var(--text-secondary)' }}>
        Derniere verification : {new Date(data.timestamp).toLocaleString('fr-FR')}
      </div>
    </div>
  );
};

export default InfrastructureDashboard;
